import { useState, useEffect } from 'react';

/**
 * Custom hook that debounces a value
 * @param {any} value - The value to debounce
 * @param {number} delay - The delay in milliseconds (default: 300)
 * @returns {any} - The debounced value
 */
export const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // Set up a timer to update the debounced value after the delay
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Clean up the timer if value changes before delay completes
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Custom hook specifically for search functionality with debouncing
 * @param {string} initialValue - The initial search value
 * @param {number} delay - The debounce delay in milliseconds (default: 300)
 * @returns {object} - Object containing search state and handlers
 */
export const useSearchDebounce = (initialValue = '', delay = 300) => {
  const [searchQuery, setSearchQuery] = useState(initialValue);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, delay);

  // Track when search is in progress (typing but not yet debounced)
  useEffect(() => {
    if (searchQuery !== debouncedSearchQuery) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }
  }, [searchQuery, debouncedSearchQuery]);

  const clearSearch = () => {
    setSearchQuery('');
  };

  const updateSearch = (value) => {
    setSearchQuery(value);
  };

  return {
    searchQuery,           // Current input value (immediate)
    debouncedSearchQuery,  // Debounced value (for actual filtering)
    isSearching,           // Whether user is typing (not yet debounced)
    updateSearch,          // Function to update search query
    clearSearch            // Function to clear search
  };
};